import { css } from 'styled-components';
import { basicTheme, createTheme } from './basicTheme';
import { candidateTheme } from './candidateTheme';
import { employerTheme } from './employerTheme';

const mainBlue = '#2849f2';

const colors = {
  primary: candidateTheme.colors.primary,
  primaryHover: '#2469dd',
  secondary: '#1377ff',
  secondaryHover: '#2469dd',
  tertiary: '#9ea2b9',
  header: {
    bg: basicTheme.colors.white,
    text: basicTheme.colors.textPrimary
  },
  gorgeusBlue: '#536ef4',
  sidebar: {
    bg: basicTheme.colors.boldPurple,
    icon: basicTheme.colors.lightPurple,
    focusedColor: '#fff',
    iconHoverBackground: '#613ea3'
  },
  checkbox: mainBlue,
  mainBlue,
  primaryFormFieldBorder: '#c9cedb',
  secondaryFormFieldBorder: '#d7dee4',
  disabled: '#cacfd4',
  combobox: {
    selected: basicTheme.colors.iron,
    get focusBorder() {
      return colors.mainBlue;
    }
  },
  multiSelectItem: {
    bg: basicTheme.colors.lightBlue,
    color: basicTheme.colors.textSecondary,
    closeIcon: basicTheme.colors.textSecondary,
    hover: {
      bg: basicTheme.colors.lightBlue
    }
  }
};

const buttons = {
  ...candidateTheme.buttons,
  tertiary: employerTheme.buttons.tertiary
};

const borders = {
  header: `1px solid ${basicTheme.colors.iceBlueTwo}`
};

const radioButtons = {
  tertiary: candidateTheme.radioButtons.tertiary,
  secondary: css`
    & > .fake-radio-button {
      min-width: 100%;
      border: 1px solid;
      border-color: ${basicTheme.colors.iceBlueTwo};
      border-radius: 4px;
      color: ${basicTheme.colors.textSecondary};
    }
    & > input:not(:disabled):not(:checked) + .fake-radio-button {
      &:hover {
        background-color: ${basicTheme.colors.lightBlue};
        border-color: ${basicTheme.colors.lightBlue};
        color: ${basicTheme.colors.textSecondary};
      }
    }
    &:not(:last-child) > .fake-radio-button {
      border-top-right-radius: 0;
      border-bottom-right-radius: 0;
    }
    &:not(:first-child) > .fake-radio-button {
      border-top-left-radius: 0;
      border-bottom-left-radius: 0;
    }
    &:not(:last-child) > input:not(:checked):not(:disabled) + .fake-radio-button {
      border-right: none;
    }
    &.checked + & > .fake-radio-button,
    &.disabled + & > input:not(:checked) + .fake-radio-button {
      border-left: none;
    }
    & > input:checked + .fake-radio-button {
      border-color: ${colors.mainBlue};
      background-color: ${basicTheme.colors.lightBlue};
    }
    & > input:disabled + .fake-radio-button {
      background-color: ${basicTheme.colors.iceBlueTwo};
      border-color: ${basicTheme.colors.cloudyBlueTwo};
      color: ${basicTheme.colors.cloudyBlueTwo};
      cursor: not-allowed;
    }
  `
};

const links = {
  subnav: css`
    color: ${basicTheme.colors.textSecondary};
    font-size: 14px;
    font-weight: ${basicTheme.fontWeights.normal};

    &.active {
      font-weight: ${basicTheme.fontWeights.bold};
      color: ${basicTheme.colors.textPrimary};
    }
    &:hover {
      color: ${basicTheme.colors.textPrimary};
    }
  `
};

const inputs = {
  primary: {
    backgroundColor: 'transparent',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: colors.primaryFormFieldBorder,
    borderRadius: '2px',
    color: colors.primary,
    '&::placeholder': {
      color: colors.tertiary
    },
    '&:disabled': {
      color: colors.disabled,
      '&::placeholder': {
        color: colors.disabled
      }
    },
    '&:focus': {
      '&::placeholder': {
        color: colors.tertiary
      }
    }
  },
  secondary: {
    backgroundColor: 'transparent',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: colors.secondaryFormFieldBorder,
    borderRadius: '4px',
    color: colors.primary,
    '&::placeholder': {
      color: colors.tertiary
    },
    '&:disabled': {
      color: colors.disabled,
      '&::placeholder': {
        color: colors.disabled
      }
    },
    '&:focus': {
      '&::placeholder': {
        color: colors.tertiary
      }
    }
  }
};

const stepProgressBars = {
  primary: css<any>`
    background-color: ${props => (props.isActive ? basicTheme.colors.lightBlue : basicTheme.colors.white)};
    border-top: 1px solid ${props => (props.isActive ? basicTheme.colors.lightBlue : basicTheme.colors.iceBlueTwo)};
    border-bottom: 1px solid ${props => (props.isActive ? basicTheme.colors.lightBlue : basicTheme.colors.iceBlueTwo)};
    &:hover {
      background-color: ${props => !props.isActive && !props.disabled && '#edfcff'};
      &:not(:last-child) {
        &:after {
          border-left-color: ${props => !props.isActive && !props.disabled && '#edfcff'};
        }
      }
    }
    &:first-child {
      border-top-left-radius: 4px;
      border-bottom-left-radius: 4px;
      border-left: 1px solid ${basicTheme.colors.iceBlueTwo};
    }
    &:last-child {
      border-top-right-radius: 4px;
      border-bottom-right-radius: 4px;
      border-right: 1px solid ${basicTheme.colors.iceBlueTwo};
    }
    &:not(:last-child) {
      &:before {
        border-left: ${props => css`
          ${props.height / 4}px solid ${props.isActive ? basicTheme.colors.lightBlue : basicTheme.colors.iceBlueTwo};
        `};
      }
      &:after {
        border-left: ${props => css`
          ${props.height / 4}px solid ${props.isActive ? basicTheme.colors.lightBlue : basicTheme.colors.white};
        `};
      }
    }
  `
};

const cards = {
  primary: {
    padding: '20px',
    backgroundColor: basicTheme.colors.white,
    borderRadius: '4px',
    border: `1px solid ${basicTheme.colors.iceBlueTwo}`,
    transition: 'box-shadow 0.35s ease-in',
    boxShadow: 'none',
    '&:hover': {
      boxShadow: '0 7px 12px 0 rgba(79, 85, 104, 0.1)'
    }
  }
};
const heights = {
  stepsbar: 84
};

const themeObject = {
  borders,
  inputs,
  buttons,
  radioButtons,
  stepProgressBars,
  colors,
  heights,
  cards,
  links
};

export const freelancerTheme = createTheme(themeObject);

const colorsTalentPool = {
  ...colors,
  primary: mainBlue
};
export const freelancerThemeTalentPool = createTheme({
  ...themeObject,
  colors: colorsTalentPool
});
